import { Component, inject, Input, OnInit } from '@angular/core';
import { PolymorphicControlComponent, PolymorphicControlService, PolyMorphicControlTypes, IPolymorphicControlMeta } from '@optima-stride-ai/optima-components';
import { FieldTypes, IFieldMeta } from '@optima-stride-ai/core';

@Component({
  selector: 'app-plan-item-edit',
  imports: [PolymorphicControlComponent],
  templateUrl: './plan-item-edit.component.html',
  styleUrl: './plan-item-edit.component.sass',
})
export class PlanItemEditComponent implements OnInit {
  @Input() date: string = '';
  @Input() expected: number = 0;
  @Input() unitSymbol: string = '';
  @Input() minDate: string = '';
  @Input() maxDate: string = '';
  @Input() minExpected: number = -Infinity;
  @Input() maxExpected: number = Infinity;
  @Input() existingDates: string[] = [];

  private readonly polymorphicControlService = inject(PolymorphicControlService);

  dateControl!: IPolymorphicControlMeta;
  expectedControl!: IPolymorphicControlMeta;

  ngOnInit(): void {
    this.dateControl = this.polymorphicControlService.getC({
      id: 'plan-item-edit-date',
      type: PolyMorphicControlTypes.calendar,
      label: 'Date',
      icon: 'cilCalendar',
      value: this.date,
      floatingLabel: true,
      minDate: this.minDate || undefined,
      maxDate: this.maxDate || undefined,
      customValidationMessages: {
        pattern: 'This date already exists in the plan',
      },
      fieldMeta: {
        id: 'plan-item-edit-date',
        name: 'Date',
        type: FieldTypes.date,
        label: 'Date',
        isRequired: true,
      },
    });

    this.expectedControl = this.polymorphicControlService.getC({
      id: 'plan-item-edit-expected',
      type: PolyMorphicControlTypes.number,
      label: this.unitSymbol ? `Expected Value (${this.unitSymbol})` : 'Expected Value',
      icon: 'cilGraph',
      value: this.expected,
      floatingLabel: true,
      fieldMeta: {
        id: 'plan-item-edit-expected',
        name: 'Expected Value',
        type: FieldTypes.number,
        label: 'Expected Value',
        isRequired: true,
        min: isFinite(this.minExpected) ? this.minExpected : undefined,
        max: isFinite(this.maxExpected) ? this.maxExpected : undefined,
      },
    });

    this.syncDuplicateDatePattern();
  }

  onDateChange(): void {
    this.syncDuplicateDatePattern();
  }

  private syncDuplicateDatePattern(): void {
    const current = this.dateControl?.value;
    if (current && this.existingDates.includes(current)) {
      // Negative-lookahead pattern that fails for exactly this date value
      this.dateControl = { ...this.dateControl, fieldMeta: { ...this.dateControl.fieldMeta, inputPattern: `^(?!${current}$).*$` } as IFieldMeta };
    } else if (this.dateControl?.fieldMeta?.inputPattern) {
      this.dateControl = { ...this.dateControl, fieldMeta: { ...this.dateControl.fieldMeta, inputPattern: '' } as IFieldMeta };
    }
  }

  get data(): { date: string; expected: number } {
    return {
      date: this.dateControl?.value ?? '',
      expected: Number(this.expectedControl?.value ?? 0),
    };
  }


  validate(): boolean {
    // Touch both controls so inline errors become visible on first Save click
    this.dateControl = { ...this.dateControl, touched: true };
    this.expectedControl = { ...this.expectedControl, touched: true };
    this.syncDuplicateDatePattern();

    const date = this.dateControl?.value;
    if (!date || isNaN(new Date(date).getTime())) {
      return false;
    }
    if (this.minDate && date < this.minDate) return false;
    if (this.maxDate && date > this.maxDate) return false;
    if (this.existingDates.includes(date)) return false;
    const expected = Number(this.expectedControl?.value);
    if (isNaN(expected)) return false;
    if (isFinite(this.minExpected) && expected < this.minExpected) return false;
    if (isFinite(this.maxExpected) && expected > this.maxExpected) return false;
    return true;
  }
}
